import {merge} from 'lodash';
import Assessment from '../assessment';
import AssessmentResult from '../../../values/AssessmentResult';

import keyphraseLengthFactor from '../../../scoring/helpers/assessments/keyphraseLengthFactor';
import {KEYPHRASE_DENSITY_ID, MAIN_CONTENT_POINTS} from '@axyseo/const/analysis';

/**
 * Represents the assessment that will look if the keyphrase density is within the recommended range.
 */
class KeyphraseDensityAssessment extends Assessment {
  /**
   *
   * @param config
   */
  constructor(config = {}) {
    super();

    const defaultConfig = {
      id: KEYPHRASE_DENSITY_ID,
      fixPosition: 'highlightKeyword',
      docUrl:
        'https://docs.avada.io/seo-suite-help-center/seo-audit/on-page-seo/checklist#main-keyword-density',
      ctaType: 'fix',
      priority: 'high',
      title: 'Main keyword density',
      content: {
        good: 'Keyword density is optimized, between 1 - 1.5%.',
        bad: 'Keep keyword density from 1% - 1.5%.',
        improve: ''
      }
    };

    this.identifier = KEYPHRASE_DENSITY_ID;
    this._config = merge(defaultConfig, config);
  }

  /**
   * Runs the keyphrase density module, based on this returns an assessment
   * result with score.
   *
   * @param {Paper} paper The paper to use for the assessment.
   * @param {Researcher} researcher The researcher used for calling the research.
   *
   * @returns {AssessmentResult} The result of the assessment.
   */
  getResult({paper, researcher}) {

    this._keyphraseCount = researcher.getResearch('getKeyphraseCount');
    const keyphraseLength = this._keyphraseCount.keyphraseLength;

    this._keyphraseDensity = researcher.getResearch('getKeyphraseDensity');
    this._keyphraseDensity = this._keyphraseDensity * keyphraseLengthFactor(keyphraseLength);

    // For collection pages: density % is meaningless on 150–300 word content
    // and conflicts with related-keyword coverage. Treat density as good when
    // the keyword is already placed in the introduction AND in a subheading.
    this._placementOverride = false;
    const pageType =
      typeof paper?.getPageType === 'function' ? paper.getPageType() : null;
    this._pageType = pageType;
    if (pageType === 'collection' || pageType === 'product') {
      const firstParagraph = researcher.getResearch('findKeywordInFirstParagraph');
      const subHeadings = researcher.getResearch('matchKeywordInSubheadings');
      const inIntro = !!(
        firstParagraph &&
        (firstParagraph.foundInOneSentence || firstParagraph.foundInParagraph)
      );
      const inSubheading = !!(subHeadings && subHeadings.matches > 0);
      this._placementOverride = inIntro && inSubheading;
    }

    const assessmentResult = new AssessmentResult({config: this._config});

    const calculatedResult = this.calculateResult(paper);

    assessmentResult.setScore(calculatedResult.score);
    assessmentResult.setStatus(calculatedResult.status);
    assessmentResult.setTitle(calculatedResult.title);
    return assessmentResult;
  }

  /**
   *
   * @returns {{title: string, score: number, status: string}}
   */
  calculateResult() {
    let status = 'bad';
    const density = this._keyphraseDensity;
    const roundedDensity = parseFloat(density.toFixed(2));
    if (roundedDensity >= 1 && roundedDensity <= 1.5) {
      status = 'good';
    }

    // Placement-based overrides (see getResult for how _placementOverride is set):
    //  - collection: short content (150–300 words) makes % unreliable; pass
    //    purely on placement (intro + subheading).
    //  - product: keep the displayed 1.0–1.5% target, but accept the wider
    //    0.8–2.0% range when placement is already correct, so refinement
    //    doesn't loop over a ±0.2% drift.
    if (this._placementOverride) {
      if (this._pageType === 'collection') {
        status = 'good';
      } else if (
        this._pageType === 'product' &&
        roundedDensity >= 0.8 &&
        roundedDensity <= 2.0
      ) {
        status = 'good';
      }
    }

    const score = this.getScore(MAIN_CONTENT_POINTS, status);
    this._config = merge(this._config, {
      roundedDensity
    });
    return {
      roundedDensity,
      score,
      status
    };
  }

  /**
   * Checks whether the paper has a text of the minimum required length and a keyphrase is set. Language-specific length requirements and methods
   * of counting text length may apply (e.g. for Japanese, the text should be counted in characters instead of words, which also makes the minimum
   * required length higher).
   *
   * @param {Paper} 		paper 		The paper to use for the assessment.
   * @param {Researcher}  researcher  The paper to use for the assessment.
   *
   * @returns {boolean} True if applicable.
   */
  isApplicable(paper, researcher) {
    return true;
  }
}

export default KeyphraseDensityAssessment;
